import {
  useFetchMyRfqQuotationsQuery,
  CapaQuotationDetailedQuotationItem,
  CapaQuotationDetailedPcbFabricationItem,
  CapaQuotationDetailedPcbAssemblyItem,
  CapaRfqPurchaseOrderTransfer,
  CapaQuotationChatRoomStatus,
  useFetchMyQuotationUserQuery
} from '@generated/graphql';

import { useSnackbar } from '@components/Provider';
import { useSelector } from '@store';
import { useEffect } from 'react';
import { useState } from 'react';

export interface PurchaseOrderBaseData {
  rfqId?: string;
  quotationId?: string;
  partnerId?: string;
  shipAddress?: string;
  shipDetailAddress?: string;
  shipDate?: string;
  inspectionPeriod?: number;
  managerName?: string;
  phone?: string;
  purchaseOrderEmail?: string;
  transferTerms?: string;
  transferStorage?: number;
  quotation?: number;
  deliveryCost?: number;
  maxDraftModificationCount?: number;
  draftModificationTerms?: string;
  transfer?: CapaRfqPurchaseOrderTransfer;
  items?: CapaQuotationDetailedQuotationItem[];
  pcbFabricationItems?: CapaQuotationDetailedPcbFabricationItem[];
  pcbAssemblyItems?: CapaQuotationDetailedPcbAssemblyItem[];
  createdAt?: string;
  latest?: boolean;
  isRejected?: boolean;
  chatRoomStatus?: CapaQuotationChatRoomStatus;
}

interface UseBaseData {
  rfqId?: string;
  partnerId?: string;
}

const useBaseData = ({
  rfqId,
  partnerId
}: UseBaseData): {
  data?: PurchaseOrderBaseData;
  loading: boolean;
  error?: boolean;
} => {
  const { enqueueSnackbar } = useSnackbar();

  const [skip, setSkip] = useState(true);

  const { data: clientQueryResult } = useFetchMyQuotationUserQuery({
    fetchPolicy: 'cache-first'
  });

  const client = clientQueryResult?.fetchMyQuotationUser.data;

  const chatRoomStatus = useSelector((state) => state.chat?.chatRoomStatus);

  const selectedQuotationId = useSelector(
    (state) => state.documents?.selectedQuotationId
  );

  let baseData: PurchaseOrderBaseData = {};

  // 첫 발주서 발행

  useEffect(() => {
    if (!rfqId || !partnerId) {
      setSkip(true);
    } else {
      setSkip(false);
    }
  }, [rfqId, partnerId]);

  const {
    data: quotationData,
    loading: quotationLoading
  } = useFetchMyRfqQuotationsQuery({
    variables: {
      input: {
        rfqId: rfqId ?? '',
        quotationPartnerId: partnerId ?? ''
      }
    },
    fetchPolicy: 'network-only',
    skip,
    onError: () =>
      enqueueSnackbar(
        '일시적인 오류로 서비스 접속에 실패했습니다. 새로고침 후 다시 시도해주세요. 새로고침 후에도 실패한 경우 채널톡으로 문의해주세요.',
        { variant: 'error' }
      )
  });

  if (!rfqId || !partnerId) {
    return {
      data: undefined,
      loading: false
    };
  }

  // 이전 견적을 불러왔거나 견적서로 부터 발주서를 작성 하는경우
  if (selectedQuotationId) {
    const quotations = quotationData?.fetchMyRfqQuotations.data ?? [];
    const quotation = quotations.find((q) => q.id === selectedQuotationId);

    baseData = {
      rfqId,
      partnerId,
      quotationId: quotation?.id,
      shipAddress: client?.fullAddress ?? '',
      shipDetailAddress: undefined,
      shipDate: '',
      inspectionPeriod: undefined,
      managerName: client?.managerName ?? '',
      phone: client?.cellphoneNumber ?? '',
      purchaseOrderEmail: client?.email,
      transferTerms: undefined,
      transferStorage: 0,
      quotation: quotation?.quotation ?? undefined,
      deliveryCost: quotation?.deliveryCost ?? undefined,
      items: quotation?.items?.map(({ __typename, ...item }) => item) ?? [],
      pcbFabricationItems:
        quotation?.pcbFabricationItems?.map(
          ({ __typename, ...item }) => item
        ) ?? [],
      pcbAssemblyItems:
        quotation?.pcbAssemblyItems?.map(({ __typename, ...item }) => item) ??
        [],
      maxDraftModificationCount:
        quotation?.maxDraftModificationCount ?? undefined,
      draftModificationTerms: quotation?.draftModificationTerms ?? undefined,
      transfer: undefined,
      latest: quotation?.latest ?? false,
      createdAt: quotation?.createdAt ?? '',
      isRejected: false,
      chatRoomStatus: chatRoomStatus ?? undefined
    };

    return {
      data: baseData,
      loading: quotationLoading
    };
  }

  const quotation = quotationData?.fetchMyRfqQuotations.data?.[0] ?? undefined;

  baseData = {
    rfqId,
    partnerId,
    quotationId: quotation?.id ?? '',
    shipAddress: client?.fullAddress ?? '',
    shipDetailAddress: undefined,
    shipDate: '',
    inspectionPeriod: undefined,
    managerName: client?.managerName ?? '',
    phone: client?.cellphoneNumber ?? '',
    purchaseOrderEmail: client?.email,
    transferTerms: undefined,
    transferStorage: 0,
    quotation: quotation?.quotation ?? undefined,
    deliveryCost: quotation?.deliveryCost ?? undefined,
    items: quotation?.items?.map(({ __typename, ...item }) => item) ?? [],
    pcbFabricationItems:
      quotation?.pcbFabricationItems?.map(({ __typename, ...item }) => item) ??
      [],
    pcbAssemblyItems:
      quotation?.pcbAssemblyItems?.map(({ __typename, ...item }) => item) ?? [],
    maxDraftModificationCount:
      quotation?.maxDraftModificationCount ?? undefined,
    draftModificationTerms: quotation?.draftModificationTerms ?? undefined,
    transfer: undefined,
    latest: quotation?.latest ?? false,
    isRejected: false,
    createdAt: quotation?.createdAt ?? '',
    chatRoomStatus: chatRoomStatus ?? undefined
  };

  return {
    data: baseData,
    loading: quotationLoading
  };
};

export default useBaseData;
